import i18next from "i18next";
import {
  AssetInErrorType,
  ChargingStationInErrorType,
  CurrentType,
  Filters,
  OCPPPhase,
  PhaseAssignmentLabelEnums,
  PhaseAssignmentValueEnums,
  TransactionInErrorType,
  UpdatedUserRole,
  UserInErrorType,
} from "../enums/enums";
import { AccessibilityType } from "../models/model";

const getConnectorLetterFromConnectorID = (connectorId: number): string => {
  return String(connectorId)
};

export const OWNER_FILTER_ITEMS = {
  CurrentOrganization: "issuer.local",
  ExternalOrganization: "issuer.foreign",
  CurrentOrgAPI: "Current Organization",
  ExternalOrgAPI: "External Organization",
};

export const DefaultPageSize = 50;
export const FilterDefaultPageSize = 1000;
// For 30-day check
export const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;
export const SIX_MONTHS_IN_MS = 6 * 30 * 24 * 60 * 60 * 1000;
export const SEVEN_DAYS_IN_MS = 7 * 24 * 60 * 60 * 1000;
export const TWENTY_FOUR_HOURS_IN_MS = 24 * 60 * 60 * 1000;
export const TOTAL_DAY_DURATION_MINS = 24 * 60;

export const CSV_SEPARATOR = ",";
export const EMPTY_PLACEHOLDER = "-";
export const CR_LF = "\r\n";
export const STRIPE_URL = "https://api.stripe.com";

export const HTTPError = {
  SITE_AREA_TREE_ERROR: 540,
  TENANT_ALREADY_EXIST: 597,
  SMART_CHARGING_STILL_ACTIVE_FOR_SITE_AREA: 538,
  SITE_AREA_TREE_ERROR_SMART_SAME_SITE_AREA: 541,
  SITE_AREA_TREE_ERROR_SITE: 542,
  SITE_AREA_TREE_ERROR_SMART_CHARGING: 543,
  SITE_AREA_TREE_ERROR_SMART_NBR_PHASES: 544,
  SITE_AREA_TREE_ERROR_VOLTAGE: 545,
  SITE_AREA_TREE_ERROR_MULTIPLE_ACTIONS_NOT_SUPPORTED: 546,
  USER_ACCOUNT_CHANGED: 595,
  TENANT_COMPONENT_CHANGED: 596,
  USER_EMAIL_ALREADY_EXIST_ERROR: 510,
  REFUND_SESSION_OTHER_USER_ERROR: 551,
  REFUND_CONNECTION_ERROR: 552,
  CRYPTO_MIGRATION_IN_PROGRESS: 511,
  CRYPTO_ALGORITHM_NOT_SUPPORTED: 512,
  CRYPTO_KEY_LENGTH_INVALID: 513,
  CRYPTO_CHECK_FAILED: 514,
  TAG_ALREADY_EXIST_ERROR: 576,
  TAG_VISUAL_ID_ALREADY_EXIST_ERROR: 577,
  TAG_INACTIVE: 579,
  CANNOT_ACQUIRE_LOCK: 510,
};

export const DEFAULT_CURRENCY_CODE = "EUR";

export const AMENITIES_STALE_TIME_MS = 5 * 60 * 1000;

export const GraphColor = {
  color_primary: "#3D3AD3",
  color_secondary: "#111111",
  color_danger: "#D32F2F",
  color_warning: "#F57C00",
  color_success: "#388E3C",
  instantPowerAmpsColor: "#1976D2",
  instantPowerAmpsL1Color: "#2196F3",
  instantPowerAmpsL2Color: "#42A5F5",
  instantPowerAmpsL3Color: "#64B5F6",
  consumptionColor: "#0097A7",
  amountColor: "#F57C00",
  stateOfChargeColor: "#388E3C",
  limitColor: "#D32F2F",
  instantVoltsColor: "#512DA8",
  instantAmpsDCColor: "#FBC02D",
  instantVoltsL1Color: "#3F51B5",
  instantVoltsL2Color: "#5C6BC0",
  instantVoltsL3Color: "#7986CB",
  netAssetConsumptionWattsColor: "#7B1FA2",
  netAssetConsumptionAmpsColor: "#9C27B0",
  defaultColor: "#3D3AD3",
  chartDefaultColor: "rgba(0, 0, 0)",
};

export const StatusCodes = {
  NOT_FOUND: 404,
  THREE_PHASE_CHARGER_ON_SINGLE_PHASE_SITE_AREA: 539,
  CHARGE_POINT_NOT_VALID: 584,
  FEATURE_NOT_SUPPORTED_ERROR: 585,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  BAD_REQUEST: 400,
  CONFLICT: 406,
  REQUEST_TIMEOUT: 408,
  MOVED_PERMANENTLY: 301,
};
export const SUPPORTED_LOCALES = Object.freeze(["en_US", "fr_FR", "es_ES", "de_DE", "pt_PT", "it_IT", "cs_CZ", "en_AU"]);

export const LOCALE_DESCRIPTION = new Map([
  ["locale_desc_en_US", "English"],
  ["locale_desc_fr_FR", "French"],
  ["locale_desc_es_ES", "Spanish"],
  ["locale_desc_de_DE", "German"],
  ["locale_desc_pt_PT", "Portuguese"],
  ["locale_desc_it_IT", "Italian"],
  ["locale_desc_cs_CZ", "Czech"],
  ["locale_desc_en_AU", "Australian"],
]);

export const phaseAssignmentToGridMapThreePhased = [
  {
    label: "ABC (L1, L2, L3)",
    value: {
      csPhaseL1: OCPPPhase.L1,
      csPhaseL2: OCPPPhase.L2,
      csPhaseL3: OCPPPhase.L3,
    },
  },
  {
    label: "BCA (L2, L3, L1)",
    value: {
      csPhaseL1: OCPPPhase.L2,
      csPhaseL2: OCPPPhase.L3,
      csPhaseL3: OCPPPhase.L1,
    },
  },
  {
    label: "CAB (L3, L1, L2)",
    value: {
      csPhaseL1: OCPPPhase.L3,
      csPhaseL2: OCPPPhase.L1,
      csPhaseL3: OCPPPhase.L2,
    },
  },
];

export const phaseAssignmentToGridMapSinglePhased = [
  {
    label: "A(L1-N)",
    value: { csPhaseL1: OCPPPhase.L1, csPhaseL2: null, csPhaseL3: null },
  },
  {
    label: "B(L2-N)",
    value: { csPhaseL1: OCPPPhase.L2, csPhaseL2: null, csPhaseL3: null },
  },
  {
    label: "C(L3-N)",
    value: { csPhaseL1: OCPPPhase.L3, csPhaseL2: null, csPhaseL3: null },
  },
  {
    label: "AB(L1-L2-G)",
    value: { csPhaseL1: OCPPPhase.L4, csPhaseL2: null, csPhaseL3: null },
  },
  {
    label: "BC(L2-L3-G)",
    value: { csPhaseL1: OCPPPhase.L5, csPhaseL2: null, csPhaseL3: null },
  },
  {
    label: "CA(L3-L1-G)",
    value: { csPhaseL1: OCPPPhase.L6, csPhaseL2: null, csPhaseL3: null },
  },
];

export const newPhaseAssignmentToGridOptionsSinglePhased = [
  {
    label: PhaseAssignmentLabelEnums.A,
    value: PhaseAssignmentValueEnums.L1_N,
  },
  {
    label: PhaseAssignmentLabelEnums.B,
    value: PhaseAssignmentValueEnums.L2_N,
  },
  {
    label: PhaseAssignmentLabelEnums.C,
    value: PhaseAssignmentValueEnums.L3_N,
  },
  {
    label: PhaseAssignmentLabelEnums.AB,
    value: PhaseAssignmentValueEnums.L1_L2_G,
  },
  {
    label: PhaseAssignmentLabelEnums.BC,
    value: PhaseAssignmentValueEnums.L2_L3_G,
  },
  {
    label: PhaseAssignmentLabelEnums.CA,
    value: PhaseAssignmentValueEnums.L3_L1_G,
  },
];

export const newPhaseAssignmentToGridOptionsThreePhased = [
  {
    label: PhaseAssignmentLabelEnums.ABC,
    value: PhaseAssignmentValueEnums.L1_L2_L3,
  },
];

/**
 * Get phase assignment options based on the number of phases
 * @param phase - Number of phases (1 for single-phase, 3 for three-phase)
 * @returns Array of phase assignment options with label and value
 */
export const getPhaseAssignmentOptions = (phase?: number | string): Array<{ label: string; value: string }> => {
  const normalizedPhase = Number(phase);
  const options =
    normalizedPhase === 1
      ? newPhaseAssignmentToGridOptionsSinglePhased
      : newPhaseAssignmentToGridOptionsThreePhased;
  return options.map(({ label, value }) => ({
    label,
    value,
  }));
};

const ListOfConnectors = () => {
  const inValue = Array.from(Array(26).keys()).map((element: number) => {
    const value = (element + 1).toString();
    const key = getConnectorLetterFromConnectorID(element + 1);
    return { key, value };
  });
  const connectorObjects = {};
  inValue.forEach((e) => {
    connectorObjects[e.key] = e.value;
  });
  return connectorObjects;
};

export const filterSelector = {
  [Filters.OWNER]: {
    apiFilter: "Issuer",
    value: { "issuer.local": true, "issuer.foreign": false },
  },
  [Filters.ROLES]: {
    apiFilter: "Role",
    value: {
      "users.role_admin": UpdatedUserRole.ADMIN,
      "users.role_agent": UpdatedUserRole.AGENT,
      "users.role_cpo_admin": UpdatedUserRole.CPO_ADMIN,
      "users.role_site_admin": UpdatedUserRole.SITE_ADMIN,
      "users.role_site_owner": UpdatedUserRole.SITE_OWNER,
    },
  },
  [Filters.STATUS]: {
    apiFilter: "Status",
    value: {
      "users.status_active": "A",
      "users.status_blocked": "B",
      "users.status_inactive": "I",
      "users.status_locked": "L",
      "users.status_pending": "P",
    },
  },
  [Filters.API_USER]: {
    apiFilter: "Technical",
    value: { "users.technical_user": true, "users.non_technical_user": false },
  },
  [Filters.INACTIVITY]: {
    apiFilter: "InactivityStatus",
    value: {
      "transactions.inactivity_warning": "W",
      "transactions.inactivity_error": "E",
    },
  },
  [Filters.LEVELS]: {
    apiFilter: "Level",
    value: {
      "logs.error": "E",
      "logs.warning": "W",
      "logs.info": "I",
      "logs.debug": "D",
    },
  },
  [Filters.SOURCES]: {
    apiFilter: "Source",
    value: {
      "Batch Server": "Batch",
      "Json Server": "Json",
      "Ocpi Server": "Ocpi",
      "OData Server": "OData",
      "Oicp Server": "Oicp",
      "Rest Server": "Rest",
      "Soap Server": "Soap",
      "Central Server": "CentralServer",
    },
  },
  // EVES-5262: changes of function filterSelector SESSION_IDENTIFIER
  [Filters.SESSION_IDENTIFIER]: {
    apiFilter: "Identifier",
    value: {
      "transactions.identifier_app": "APP",
      "transactions.identifier_rfid": "RFID",
      "transactions.identifier_ivr": "IVR",
    },
  },
  [Filters.SMART_CHARGING]: {
    apiFilter: "SmartCharging",
    value: {
      "Load Balancing": "loadBalancing",
      "Demand management": "demandManagement",
      "Not smart charging": "NoSmartCharging",
    },
  },
  [Filters.ACCESS_MODE]: {
    apiFilter: "FreeAccess",
    value: {
      "users.user_with_freeAccess": true,
      "users.user_without_freeAccess": false,
    },
  },
  [Filters.ERROR]: {
    apiFilter: "ErrorType",
    value: {
      "users.errors.failed_billing_synchro.title": "failed_billing_synchro",
      "users.errors.inactive_user_account.title": "inactive_user_account",
      "users.errors.inactive_user.title": "inactive_user",
      "users.errors.unassigned_user.title": "unassigned_user",
    },
  },
  [Filters.CHARGINGSTATIONERROR]: {
    apiFilter: "ErrorType",
    value: {
      "chargers.errors.connector_error.title": "connector_error",
      "chargers.errors.missing_settings.title": "missing_settings",
      "chargers.errors.missing_site_area.title": "missing_site_area",
      "chargers.errors.connection_broken.title": "connection_broken",
    },
  },
  [Filters.ASSET]: {
    apiFilter: "ErrorType",
    value: {
      "chargers.errors.missing_site_area.title": "missing_site_area",
    },
  },
  [Filters.RFIDSTATUS]: {
    apiFilter: "Status",
    value: { "tags.activated": "A", "tags.deactivated": "I" },
  },
  [Filters.SESSION_REFUNDING_STATUS]: {
    refundStatus: "RefundStatus",
    value: {
      "transactions.refund_undefined": "notSubmitted",
      "transactions.refund_submitted": "submitted",
      "transactions.refund_cancelled": "cancelled",
      "transactions.refund_approved": "approved",
    },
  },
  [Filters.NEWCHARGINGTAB]: {
    apiFilter: "Status",
    value: { Registered: "registered", Installed: "installed" },
  },
  [Filters.CONNECTORS]: {
    apiFilter: "Connectors",
    value: ListOfConnectors(),
  },
  [Filters.INERROR_ERROR]: {
    apiFilter: "ErrorType",
    value: {
      "transactions.errors.average_consumption_greater_than_connector_capacity.title": "average_consumption_greater_than_connector_capacity",
      "transactions.errors.long_inactivity.title": "long_inactivity",
      "transactions.errors.incorrect_starting_date.title": "incorrect_starting_date",
      "transactions.errors.low_consumption.title": "low_consumption",
      "transactions.errors.low_duration.title": "low_duration",
      "transactions.errors.missing_price.title": "missing_price",
      "transactions.errors.negative_duration.title": "negative_inactivity",
      "transactions.errors.negative_inactivity.title": "negative_inactivity",
      "transactions.errors.no_consumption.title": "no_consumption",
      "transactions.errors.no_billing_data.title": "no_billing_data",
      "transactions.errors.missing_user.title": "missing_user",
    },
  },
  [Filters.INVOICE_STATUS]: {
    apiFilter: "Status",
    value: {
      "invoices.status.paid": "paid",
      "invoices.status.failed": "failed",
      "invoices.status.declined": "declined",
      "invoices.status.pending": "pending",
      "invoices.status.unbilled": "unbilled",
      // "invoices.status.unpaid": "open",
      // "invoices.status.draft": "draft",
      // "invoices.status.deleted": "deleted",
      // "invoices.status.uncollectible": "uncollectible",
      // "invoices.status.void": "void",
    },
  },
  [Filters.TRANSFER_STATUS]: {
    apiFilter: "Status",
    value: {
      "transfers.status.draft": "draft",
      "transfers.status.review": "review",
      "transfers.status.finalized": "finalized",
      "transfers.status.transferred": "transferred",
    },
  },
  [Filters.TRANSACTION_STATUS]: {
    apiFilter: "Status",
    value: {
      "invoices.status.paid": "paid",
      "invoices.status.unbilled": "unbilled",
      // "invoices.status.failed": "failed",
      // "invoices.status.declined": "declined",
      // "invoices.status.pending": "pending",  
      // "invoices.status.unpaid": "open",
      // "invoices.status.draft": "draft",
      // "invoices.status.deleted": "deleted",
      // "invoices.status.uncollectible": "uncollectible",
      // "invoices.status.void": "void",
    },
  },
  [Filters.CHARGINGSTATION_STATUS]: {
    apiFilter: "Status",
    value: {
      Connected: "Connected",
      Commissioned: "Commissioned",
      Active: "Active",
      Inactive: "Inactive",
      RMA: "RMA",
    },
  },
  [Filters.SITES]: {
    apiFilter: "Site",
    value: { "issuer.local": true, "issuer.foreign": false },
  },
  [Filters.BILLING_INERROR]: {
    apiFilter: "ErrorType",
    value: {
      APPLICATION: "application",
      CONFIGURATION: "configuration_error",
      ACCOUNT: "account",
      PENDING: "pending",
    },
  },
};

export const UserInErrorTypeDetail = {
  [UserInErrorType.FAILED_BILLING_SYNCHRO]: {
    title: "users.errors.failed_billing_synchro.title",
    description: "users.errors.failed_billing_synchro.description",
    action: "users.errors.failed_billing_synchro.action",
  },
  [UserInErrorType.INACTIVE_USER_ACCOUNT]: {
    title: "users.errors.inactive_user_account.title",
    description: "users.errors.inactive_user_account.description",
    action: "users.errors.inactive_user_account.action",
  },
  [UserInErrorType.NOT_ACTIVE]: {
    title: "users.errors.inactive_user.title",
    description: "users.errors.inactive_user.description",
    action: "users.errors.inactive_user.action",
  },
  [UserInErrorType.NOT_ASSIGNED]: {
    title: "users.errors.unassigned_user.title",
    description: "users.errors.unassigned_user.description",
    action: "users.errors.unassigned_user.action",
  },
};

export const ChargingStationInErrorTypeDetail = {
  [ChargingStationInErrorType.MISSING_SETTINGS]: {
    title: "chargers.errors.missing_settings.title",
    description: "chargers.errors.missing_settings.description",
    action: "chargers.errors.missing_settings.action",
  },
  [ChargingStationInErrorType.CONNECTION_BROKEN]: {
    title: "chargers.errors.connection_broken.title",
    description: "chargers.errors.connection_broken.description",
    action: "chargers.errors.connection_broken.action",
  },
  [ChargingStationInErrorType.MISSING_SITE_AREA]: {
    title: "chargers.errors.missing_site_area.title",
    description: "chargers.errors.missing_site_area.description",
    action: "chargers.errors.missing_site_area.action",
  },
  [ChargingStationInErrorType.CONNECTOR_ERROR]: {
    title: "chargers.errors.connector_error.title",
    description: "chargers.errors.connector_error.description",
    action: "chargers.errors.connector_error.action",
  },
};

export const AssetInErrorTypeDetail = {
  [AssetInErrorType.MISSING_SITE_AREA]: {
    title: "chargers.errors.missing_site_area.title",
    description: "The asset needs to be assigned to a site area",
    action: "Edit the asset and maintain the site area",
  },
};

export const TransactionInErrorTypeDetail = {
  [TransactionInErrorType.NO_CONSUMPTION]: {
    title: "transactions.errors.no_consumption.title",
    description: `${i18next.t("transactions.errors.no_consumption.description")}`,
    action: `${i18next.t("transactions.errors.no_consumption.action")}`,
  },
  [TransactionInErrorType.LOW_CONSUMPTION]: {
    title: "transactions.errors.low_consumption.title",
    description: `${i18next.t("transactions.errors.low_consumption.description")}`,
    action: `${i18next.t("transactions.errors.low_consumption.action")}`,
  },
  [TransactionInErrorType.OVER_CONSUMPTION]: {
    title: "transactions.errors.average_consumption_greater_than_connector_capacity.title",
    description: `${i18next.t("transactions.errors.average_consumption_greater_than_connector_capacity.description")}`,
    action: `${i18next.t("transactions.errors.average_consumption_greater_than_connector_capacity.action")}`,
  },
  [TransactionInErrorType.NEGATIVE_INACTIVITY]: {
    title: "transactions.errors.negative_inactivity.title",
    description: `${i18next.t("transactions.errors.negative_inactivity.description")}`,
    action: `${i18next.t("transactions.errors.negative_inactivity.action")}`,
  },
  [TransactionInErrorType.LONG_INACTIVITY]: {
    title: "transactions.errors.long_inactivity.title",
    description: `${i18next.t("transactions.errors.long_inactivity.description")}`,
    action: `${i18next.t("transactions.errors.long_inactivity.action")}`,
  },
  [TransactionInErrorType.NEGATIVE_DURATION]: {
    title: "transactions.errors.negative_duration.title",
    description: `${i18next.t("transactions.errors.negative_duration.description")}`,
    action: `${i18next.t("transactions.errors.negative_duration.action")}`,
  },
  [TransactionInErrorType.LOW_DURATION]: {
    title: "transactions.errors.low_duration.title",
    description: `${i18next.t("transactions.errors.low_duration.description")}`,
    action: `${i18next.t("transactions.errors.low_duration.action")}`,
  },
  [TransactionInErrorType.MISSING_PRICE]: {
    title: "transactions.errors.missing_price.title",
    description: `${i18next.t("transactions.errors.missing_price.description")}`,
    action: `${i18next.t("transactions.errors.missing_price.action")}`,
  },
  [TransactionInErrorType.INVALID_START_DATE]: {
    title: "transactions.errors.incorrect_starting_date.title",
    description: `${i18next.t("transactions.errors.incorrect_starting_date.description")}`,
    action: `${i18next.t("transactions.errors.incorrect_starting_date.action")}`,
  },
  [TransactionInErrorType.MISSING_USER]: {
    title: "transactions.errors.missing_user.title",
    description: `${i18next.t("transactions.errors.missing_user.description")}`,
    action: `${i18next.t("transactions.errors.missing_user.action")}`,
  },
  [TransactionInErrorType.NO_BILLING_DATA]: {
    title: "transactions.errors.no_billing_data.title",
    description: `${i18next.t("transactions.errors.no_billing_data.description")}`,
    action: `${i18next.t("transactions.errors.no_billing_data.action")}`,
  },
};

export const INFINITE_RECORDS = -1;

export const LogLevels = [
  { key: "E", value: "logs.error", type: "Faulted" },
  { key: "W", value: "logs.warning", type: "Pending" },
  { key: "I", value: "logs.info", type: "Available" },
  { key: "D", value: "logs.debug", type: "Reserved" },
];

export const URL_PATTERN = /^(?:https?|wss?):\/\/((?:[\w-]+)(?:\.[\w-]+)*)(?:[\w.,@?^=%&amp;:\\/~+#-]*[\w@?^=%&amp;\\/~+#-])?$/;

export const currencyOptions = [
  { label: "Australian Dollar", value: "AUD" },
  { label: "Brazilian Real", value: "BRL" },
  { label: "Canadian Dollar", value: "CAD" },
  { label: "Czech Koruna", value: "CZK" },
  { label: "Danish Krone", value: "DKK" },
  { label: "Euro", value: "EUR" },
  { label: "Hong Kong Dollar", value: "HKD" },
  { label: "Hungarian Forint", value: "HUF" },
  { label: "Israeli New Sheqel", value: "ILS" },
  { label: "Japanese Yen", value: "JPY" },
  { label: "Malaysian Ringgit", value: "MYR" },
  { label: "Mexican Peso", value: "MXN" },
  { label: "Norwegian Krone", value: "NOK" },
  { label: "New Zealand Dollar", value: "NZD" },
  { label: "Philippine Peso", value: "PHP" },
  { label: "Polish Zloty", value: "PLN" },
  { label: "Pound Sterling", value: "GBP" },
  { label: "Singapore Dollar", value: "SGD" },
  { label: "Swedish Krona", value: "SEK" },
  { label: "Swiss Franc", value: "CHF" },
  { label: "Taiwan New Dollar", value: "TWD" },
  { label: "Thai Baht", value: "THB" },
  { label: "Turkish Lira", value: "TRY" },
  { label: "U.S. Dollar", value: "USD" },
];

export const imageTypeOptions = [
  { label: "JPG", value: "jpg" },
  { label: "PNG", value: "png" },
  { label: "SVG", value: "svg" },
  { label: "GIF", value: "gif" },
];

export const categoryImageOptions = [
  { label: "Charger", value: "CHARGER" },
  { label: "Entrance", value: "ENTRANCE" },
  { label: "Location", value: "LOCATION" },
  { label: "Network", value: "NETWORK" },
  { label: "Operator", value: "OPERATOR" },
  { label: "Owner", value: "OWNER" },
  { label: "Other", value: "OTHER" },
];

export const constMinDivisorBar = 40;
export const constMinDivisorPie = 40;

export class ChartConstants {
  public static STACKED_ITEM = "item";
  public static STACKED_TOTAL = "total";
}

export const languageLocale = {
  cs: "cs-CS",
  fr: "fr-FR",
  es: "es-ES",
  de: "de-DE",
  pt: "pt-BR",
  it: "it-IT",
  en: "en-US",
};

export const logConstantURLs = {
  system: process.env.REACT_APP_SYSTEM_LOG,
  application: process.env.REACT_APP_APPLICATION_LOG,
};

// Production configuration for refresh token
export const PROD_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes
export const PROD_CHECK_INTERVAL = 30000; // 30 seconds
// Define the base voltage values
export const VOLTAGE_VALUES = [400, 380, 346, 277, 240, 230, 220, 208, 120, 110];

export type NUMBER_OF_PHASE = 1 | 3;

export const SINGLE_PHASE_VOLTAGES = [208, 220, 230, 240];

export const THIRD_PHASE_VOLTAGES = [208, 240, 380, 400, 480, 600];

export const supplyVoltageOptions = [208, 220, 230, 240, 380, 400, 480, 600];

export const CHARGER_INSTALLATION_STEPS = ["Validate Token", "Site & Site Area", "Charger", "Connectors", "Summary"];

export const CHARGER_TYPE_OPTIONS = [CurrentType.AC, CurrentType.DC].map((currentType) => ({
  label: currentType,
  value: currentType,
}));

/* 
EVES-3303: [UI] Implement Enhanced Charger Template Configuration System in Super Admin Portal
Author: Anitha J
Date: 05/11/2025
 */

export const powerLimitUnits = ["WATT", "AMP"];
export const availableOcppVersions = ["1.6", "2.0.1", "2.1"];

// JIRA: [EVES-4137] -charger editor and properties
// Author: Anitha
// Date: 19/01/2026

export const capabilityGroups = [
  "Static Limitation",
  "Charging Profiles",
  "Payment Terminal",
  "Unlock Connector",
  "RFID Card",
  "Reservation",
  "Firmware Upgrade",
  "Idle Fee",
  "Fee-LCD Push",
  "Time-Based Charging",
];

export const technicalSettings = ["Master Slave", "Charge In Parallel", "Share Power To All Connectors"];

export const capabilityKeyMap: Record<string, string> = {
  "Static Limitation": "staticLimitation",
  "Charging Profiles": "chargingProfiles",
  "Payment Terminal": "paymentTerminal",
  "Unlock Connector": "unlockConnector",
  "RFID Card": "rfidCard",
  Reservation: "reservation",
  "Firmware Upgrade": "firmwareUpgrade",
  "Idle Fee": "idleFee",
  "Fee-LCD Push": "feeLCDPush",
  "Time-Based Charging": "timeBasedCharging",
};

export const technicalKeyMap: Record<string, string> = {
  "Master Slave": "masterSlaveMode",
  "Charge In Parallel": "chargerInParallel",
  "Share Power To All Connectors": "sharePowerToAllConnectors",
};

export const ACCESSIBILITY_OPTIONS: {
  label: string;
  value: AccessibilityType;
}[] = [
  { label: "Public", value: AccessibilityType.PUBLIC },
  { label: "Private", value: AccessibilityType.PRIVATE },
];

export const POWER_LIMIT_UNIT_OPTIONS = [
  { label: "WATT", value: "WATT" },
  { label: "AMP", value: "AMP" },
];

export const normalizePowerUnit = (unit?: string | null) => {
  if (!unit) return null;

  const v = unit.trim().toUpperCase();

  if (v === "W" || v === "WATT") return "WATT";
  if (v === "A" || v === "AMP") return "AMP";

  return null;
};

export const powerUnitLabel = (unit?: string | null) => {
  const v = unit?.trim().toUpperCase();

  if (v === "WATT") return "WATT";
  if (v === "AMP") return "AMP";

  return "-";
};

// Update the TEMPLATE_STATUS to use 'as const' for literal types
export const TEMPLATE_STATUS = {
  PENDING: "Pending",
  IN_PROGRESS: "InProgress",
  COMPLETED: "Completed",
  FAILED: "Failed",
} as const;

// Create a type from the values
export type TemplateStatus = typeof TEMPLATE_STATUS[keyof typeof TEMPLATE_STATUS];

// Update the arrays to use the proper type
export const RUNNING_TEMPLATE_STATUSES: TemplateStatus[] = [TEMPLATE_STATUS.PENDING, TEMPLATE_STATUS.IN_PROGRESS];

export const REAPPLY_ALLOWED_STATUSES: TemplateStatus[] = [TEMPLATE_STATUS.COMPLETED, TEMPLATE_STATUS.FAILED];

// Update normalizeTemplateStatus to return the proper type
export const normalizeTemplateStatus = (status?: string): TemplateStatus | undefined => {
  if (!status) return undefined;

  const s = status.replace(/\s+/g, "").toLowerCase();

  if (s === "pending") return TEMPLATE_STATUS.PENDING;
  if (s === "inprogress") return TEMPLATE_STATUS.IN_PROGRESS;
  if (s === "completed") return TEMPLATE_STATUS.COMPLETED;
  if (s === "failed") return TEMPLATE_STATUS.FAILED;

  return undefined;
};

export const ChargerMath = {
  /* ---------- AC FORMULAS  ---------- */

  acRatedPower: (voltage?: number, ratedAmp?: number, phase?: number): number | undefined => {
    if (ratedAmp != null && voltage != null && phase != null && ratedAmp >= 0 && voltage > 0) {
      const factor = phase === 3 ? Math.sqrt(3) : 1;
      const p = (factor * voltage * ratedAmp) / 1000;
      return roundToTwoDecimals(p); // UI display only (7.62)
    }
    return undefined;
  },

  //  returns exact watts for payload
  acRatedPowerW: (voltage?: number, ratedAmp?: number, phase?: number): number | undefined => {
    if (ratedAmp != null && voltage != null && phase != null && ratedAmp >= 0 && voltage > 0) {
      const factor = phase === 3 ? Math.sqrt(3) : 1;
      return factor * voltage * ratedAmp; // 7621.02 - no rounding
    }
    return undefined;
  },

  acConfiguredPower: (voltage?: number, amperage?: number, phase?: number): number | undefined => {
    if (amperage && voltage && phase && amperage > 0 && voltage > 0) {
      const factor = phase === 3 ? Math.sqrt(3) : 1;
      const p = (factor * voltage * amperage) / 1000;
      return roundToTwoDecimals(p); // UI display only (7.62)
    }
    return undefined;
  },

  //returns exact watts for payload
  acConfiguredPowerW: (voltage?: number, amperage?: number, phase?: number): number | undefined => {
    if (amperage && voltage && phase && amperage > 0 && voltage > 0) {
      const factor = phase === 3 ? Math.sqrt(3) : 1;
      return factor * voltage * amperage; // 7621.02 - no rounding
    }
    return undefined;
  },

  /* ---------- DC FORMULAS  ---------- */

  dcRatedAmp: (ratedPower?: number, voltage?: number, phase?: number): number | undefined => {
    if (ratedPower != null && voltage != null && phase != null && ratedPower >= 0 && voltage > 0) {
      const factor = phase === 3 ? Math.sqrt(3) : 1;
      const a = (ratedPower * 1000) / (factor * voltage);
      return roundToTwoDecimals(a);
    }
    return undefined;
  },

  dcConfiguredAmp: (power?: number, voltage?: number, phase?: number): number | undefined => {
    if (power && voltage && power > 0 && voltage > 0) {
      const factor = phase === 3 ? Math.sqrt(3) : 1;
      const a = (power * 1000) / (factor * voltage);
      return roundToTwoDecimals(a);
    }
    return undefined;
  },

  /* ---------- PHASE AUTO MAP  ---------- */

  autoPhaseAssignment: (phase: number) => (phase === 1 ? { csPhaseL1: "L1", csPhaseL2: null, csPhaseL3: null } : { csPhaseL1: "L1", csPhaseL2: "L2", csPhaseL3: "L3" }),
};

export const capabilityLabelMap: Record<string, string> = {
  supportStaticLimitation: "Support Static Limitation",
  supportChargingProfiles: "Support Charging Profiles",
  supportCreditCard: "Support Credit Card",
  supportSlave: "Support Slave",
  supportUnlockConnector: "Support Unlock Connector",
  supportReservation: "Support Reservation",
  supportRFIDCard: "Support RFID Card",
  supportFirmwareUpgrade: "Support Firmware Upgrade",
  supportIdleFee: "Support Idle Fee",
  supportPricingLCDPush: "Support Pricing Fee LCD Push",
  supportRemoteStartStopTransaction: "Support Remote Start Stop Transaction",
  supportTimeBasedCharging: "Support Time-Based Charging",
};

export const ALL_OCPP_FEATURES = ["Core", "FirmwareManagement", "LocalAuthListManagement", "RemoteTrigger", "Reservation", "SmartCharging"];

export const normalizeFeature = (value: string) => {
  if (value === "RemoteTrigge") return "RemoteTrigger";
  return value;
};

export const allowOnlyNumbers = (value: string): string => {
  let sanitized = value.replace(/[^0-9.]/g, "");

  const parts = sanitized.split(".");
  if (parts.length > 2) {
    sanitized = `${parts[0]}.${parts.slice(1).join("")}`;
  }

  return sanitized;
};

export const formatTwoDecimalsOnType = (value: string): string => {
  // remove invalid chars
  let sanitized = value.replace(/[^0-9.]/g, "");

  // allow only one dot
  const parts = sanitized.split(".");
  if (parts.length > 2) {
    sanitized = `${parts[0]}.${parts.slice(1).join("")}`;
  }

  // limit decimals to 2
  if (parts.length === 2) {
    sanitized = `${parts[0]}.${parts[1].slice(0, 2)}`;
  }

  return sanitized;
};

export const roundToTwoDecimals = (value?: number): number | undefined => {
  if (value === undefined || isNaN(value)) return undefined;
  return Number(value.toFixed(2));
};

export const removeTrailingDot = (value?: string | number | null): string => {
  if (value === undefined || value === null) return "";

  const str = String(value);

  return str.endsWith(".") ? str.slice(0, -1) : str;
};

export const normalizeTrailingDotOnBlur = (value: unknown, onChange: (value: string) => void): boolean => {
  // Tab navigation fires blur, so only write back when the visible value truly changes.
  if (typeof value !== "string" || !value.endsWith(".")) return false;

  onChange(removeTrailingDot(value));
  return true;
};

export const isSameNumberInputValue = (currentValue: unknown, nextValue: unknown): boolean => {
  if (currentValue === nextValue) return true;
  if ((currentValue === "" || currentValue == null) && (nextValue === "" || nextValue == null)) return true;

  const currentNumber = Number(currentValue);
  const nextNumber = Number(nextValue);

  return currentValue !== "" && nextValue !== "" && !Number.isNaN(currentNumber) && !Number.isNaN(nextNumber) && currentNumber === nextNumber;
};

export const normalizeOnBlurNumberInput = (value?: string | number): string => {
  if (value === undefined || value === null) return "";

  return removeTrailingDot(String(value));
};

// export const BillingErrorTypeDetail = {
//   APPLICATION_ERROR: {
//     title: "Application",
//     description: "Unknown Error Code (Non-stripe)",
//     action: "Contact support for assistance",
//   },
//   PLATFORM_SERVER_ERROR: {
//     title: "Server",
//     description:
//       "Errors 500 or greater, server is down or network issues, unexpected error code",
//     action: "Retry later or contact support if issue persists",
//   },
//   PLATFORM_APPLICATION_ERROR: {
//     title: "Platform",
//     description:
//       "Stripe error with respective error code (e.g. declined payment)",
//     action: "Review payment details and try again",
//   },
//   PLATFORM_PAYMENT_ERROR: {
//     title: "Payment",
//     description: "Payment/card error (missing payment code)",
//     action: "Verify payment information and try again",
//   },
//   CONFIGURATION_ERROR: {
//     title: "Configuration",
//     description:
//       "Platform fee misconfiguration resulting in invalid transfers calculation",
//     action: "Contact administrator to review platform fee settings",
//   },
//   MISSING_CONNECTED_ACCOUNT: {
//     title: "account",
//     description:
//       "Transaction with platform fees but no connected account association",
//     action: "Configure connected account in platform settings",
//   },
// };

export const billingTypeDetail = (errorReason: string) => {
  switch (errorReason) {
    case "invalid_transfer_fee":
    case "missing_price":
      return {
        title: "Confirm Platform Fee Configuration",
        description: "Before re-processing, you must ensure the correct platform fee has been updated in the billing section.",
        action: "Yes, Reprocess Transaction",
        cancelAction: "No, Cancel",
        onAction: "handlePayment",
      };
    case "missing_account_fee":
    case "missing_connected_account":
    case "missing_cpo_connected_account":
    case "missing_site_connected_account":
      return {
        title: "Reprocess",
        description: "Make sure connected account has been configured properly for the site before reprocessing the transactions.",
        action: "Process",
        cancelAction: "Cancel",
        onAction: "handleReprocess",
      };
    case "billing_sync_task_failed":
      return {
        title: "Retry Billing",
        description: "We will try to regenerate bill for the session <sessionID>",
        action: "Process",
        cancelAction: "Cancel",
        onAction: "handleRetry",
      };
    default:
      return {
        title: "",
        description: "",
        action: "",
        cancelAction: "",
        onAction: "",
      };
  }
};

export const DEFAULT_CONNECTOR_CONFIG = {
  AC: {
    phase: 3,
    voltage: 380,
  },
  DC: {
    phase: 1,
    voltage: 208,
  },
};

export const resolvePhaseAndVoltage = ({ chargerType, backendPhase, backendVoltage }: { chargerType: "AC" | "DC"; backendPhase?: number; backendVoltage?: number }) => {
  // Phase fallback → charger type
  const phase = backendPhase ?? (chargerType === "AC" ? 3 : 1);

  //  Voltage list → phase
  const validVoltages = phase === 3 ? THIRD_PHASE_VOLTAGES : SINGLE_PHASE_VOLTAGES;

  //  Voltage fallback → phase
  const voltage = backendVoltage && validVoltages.includes(backendVoltage) ? backendVoltage : validVoltages[0];

  return { phase, voltage };
};

// faultTrends

export const FAULT_TYPES = [
  {
    key: "ConnectorLockFailure",
    label: "Connector Lock Failure",
    color: "#F5D699",
  },
  {
    key: "EVCommunicationError",
    label: "EV Communication Error",
    color: "#91B2F2",
  },
  {
    key: "GroundFailure",
    label: "Ground Failure",
    color: "#E6E6E6",
  },
  {
    key: "HighTemperature",
    label: "High Temperature",
    color: "#F29D9D",
  },
  {
    key: "OverCurrentFailure",
    label: "Over Current Failure",
    color: "#F27A7A",
  },
  {
    key: "PowerMeterFailure",
    label: "Power Meter Failure",
    color: "#F09A65",
  },
];

// y-axis fixed limits
export const FAULT_Y_AXIS = {
  MIN: 0,
  MAX: 10,
  STEP: 2,
};

export const errorCategoryMapping = {
  application: "APPLICATION",
  configuration_error: "CONFIGURATION",
  account: "ACCOUNT",
  pending: "PENDING",
};

export const errorStatusMapping = {
  inprogress: "In Progress",
  InError: "In Error",
  retry: "Retry",
  resolved: "Resolved",
};
